import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Download, MessageCircle, PackageSearch } from "lucide-react";
import CatalogItemCard from "./CatalogItemCard.jsx";
import { itemEnquiryText, whatsappUrl } from "./catalogUtils.js";

/**
 * El catálogo de un proyecto tal como lo ve el lead, el cliente o el usuario
 * (C3D-38): cabecera con para quién es y el contacto, y la rejilla de fichas.
 *
 * Lo usan el enlace público (`/catalogo/:token`) y el perfil del usuario
 * (`/profile/proyectos/:id`). Cada página decide cómo se abre el 3D, de dónde
 * sale el PDF y si hay acciones de más por ficha (`renderExtraAction`).
 */
/**
 * Contenedor con su propio scroll: el layout de la app fija la altura de las
 * páginas (igual que en la tienda), así que sin esto el catálogo no baja.
 */
export function CatalogScroll({ children }) {
    return <div className="w-full h-full min-h-0 overflow-y-auto">{children}</div>;
}

CatalogScroll.propTypes = { children: PropTypes.node };

export default function CatalogView({ catalog, onView3d, pdfUrl, onDownloadPdf, renderExtraAction, headerSlot = null }) {
    const phone = catalog.contact?.whatsapp_phone;
    const contactName = catalog.contact?.name;
    const generalText = `Hola${contactName ? ` ${contactName}` : ""}, he visto el catálogo "${catalog.name}" y quiero hablar de un pedido.`;
    const items = useMemo(() => catalog.items || [], [catalog.items]);

    // Filtro por tipo, solo si el catálogo mezcla varios.
    const [typeFilter, setTypeFilter] = useState("");
    const types = useMemo(() => {
        const seen = new Map();
        items.forEach((item) => {
            const current = seen.get(item.product_type);
            seen.set(item.product_type, { label: item.product_label, count: (current?.count || 0) + 1 });
        });
        return [...seen.entries()].map(([value, data]) => ({ value, ...data }));
    }, [items]);
    const visibleItems = typeFilter ? items.filter((item) => item.product_type === typeFilter) : items;
    const with3d = items.filter((item) => item.has_3d).length;

    return (
        <CatalogScroll>
        <div className="w-full max-w-7xl mx-auto px-4 py-6 md:py-10 flex flex-col gap-6">
            {headerSlot}
            <header className="grid gap-6 md:grid-cols-[1fr_auto] md:items-end">
                <div className="flex flex-col gap-3 max-w-2xl">
                    {catalog.customer_name && (
                        <p className="text-sm font-medium text-base-content/70">Preparado para {catalog.customer_name}</p>
                    )}
                    <h1 className="text-3xl md:text-5xl font-bold tracking-tight leading-[1.1] text-base-content">
                        {catalog.name}
                    </h1>
                    {catalog.description && (
                        <p className="text-base text-base-content/75 leading-relaxed max-w-[65ch] whitespace-pre-line">
                            {catalog.description}
                        </p>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[14rem]">
                    <a
                        href={whatsappUrl(phone, generalText)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary gap-2"
                    >
                        <MessageCircle className="w-4 h-4" aria-hidden="true" /> Hablar por WhatsApp
                    </a>
                    {(pdfUrl || onDownloadPdf) && (
                        pdfUrl ? (
                            <a href={pdfUrl} className="btn btn-outline gap-2" download>
                                <Download className="w-4 h-4" aria-hidden="true" /> Descargar PDF
                            </a>
                        ) : (
                            <button type="button" className="btn btn-outline gap-2" onClick={onDownloadPdf}>
                                <Download className="w-4 h-4" aria-hidden="true" /> Descargar PDF
                            </button>
                        )
                    )}
                    {contactName && (
                        <p className="text-xs text-base-content/60 sm:self-center md:self-start">Te atiende {contactName}</p>
                    )}
                </div>
            </header>

            {items.length > 0 && (
                <div className="flex flex-col gap-3 border-t border-base-content/10 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-base-content/70">
                        {items.length} {items.length === 1 ? "modelo" : "modelos"}
                        {with3d > 0 ? `, ${with3d} para ver en 3D` : ""}
                    </p>
                    {types.length > 1 && (
                        <div role="group" aria-label="Filtrar por tipo" className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0">
                            {[{ value: "", label: "Todos", count: items.length }, ...types].map((type) => (
                                <button
                                    key={type.value || "all"}
                                    type="button"
                                    aria-pressed={typeFilter === type.value}
                                    onClick={() => setTypeFilter(type.value)}
                                    className={`btn btn-sm rounded-full shrink-0 ${typeFilter === type.value ? "btn-neutral" : "btn-ghost border border-base-content/15"}`}
                                >
                                    {type.label}
                                    <span className="text-xs opacity-70 tabular-nums">{type.count}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {items.length === 0 ? (
                <div className="flex flex-col items-center text-center gap-3 py-16 rounded-xl border border-dashed border-base-content/20">
                    <PackageSearch className="w-10 h-10 text-base-content/40" aria-hidden="true" />
                    <p className="font-semibold text-base-content">Todavía no hay modelos en este catálogo</p>
                    <p className="text-sm text-base-content/70 max-w-sm">Escríbenos por WhatsApp y te contamos qué estamos preparando.</p>
                </div>
            ) : (
                <section aria-label="Modelos del catálogo" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 md:gap-5 pb-8">
                    {visibleItems.map((item, index) => (
                        <CatalogItemCard
                            key={item.id}
                            item={item}
                            index={index}
                            onView3d={() => onView3d?.(item)}
                            enquiryUrl={whatsappUrl(phone, itemEnquiryText(catalog.name, item))}
                            extraAction={renderExtraAction ? renderExtraAction(item) : null}
                        />
                    ))}
                </section>
            )}
        </div>
        </CatalogScroll>
    );
}

CatalogView.propTypes = {
    catalog: PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.string,
        customer_name: PropTypes.string,
        contact: PropTypes.shape({ name: PropTypes.string, whatsapp_phone: PropTypes.string }),
        items: PropTypes.array,
    }).isRequired,
    onView3d: PropTypes.func,
    pdfUrl: PropTypes.string,
    onDownloadPdf: PropTypes.func,
    renderExtraAction: PropTypes.func,
    headerSlot: PropTypes.node,
};

/** Esqueleto con la forma del catálogo mientras carga. */
export function CatalogSkeleton() {
    return (
        <div className="w-full max-w-7xl mx-auto px-4 py-6 md:py-10 flex flex-col gap-8" aria-busy="true" aria-label="Cargando catálogo">
            <div className="flex flex-col gap-3 max-w-2xl">
                <div className="skeleton h-4 w-48" />
                <div className="skeleton h-12 w-full max-w-lg" />
                <div className="skeleton h-4 w-full max-w-md" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 md:gap-5">
                {[0, 1, 2, 3].map((key) => (
                    <div key={key} className="rounded-xl border border-base-content/10 overflow-hidden">
                        <div className="skeleton aspect-[4/3] rounded-none" />
                        <div className="p-4 flex flex-col gap-2">
                            <div className="skeleton h-3 w-20" />
                            <div className="skeleton h-5 w-40" />
                            <div className="skeleton h-8 w-full mt-2" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/** Pantalla de catálogo que no existe o ya no está disponible. */
export function CatalogUnavailable({ title = "Este catálogo ya no está disponible", text, action = null }) {
    return (
        <div className="w-full min-h-[60dvh] flex items-center justify-center px-4">
            <div className="max-w-md text-center flex flex-col items-center gap-3">
                <PackageSearch className="w-12 h-12 text-base-content/40" aria-hidden="true" />
                <h1 className="text-2xl font-bold text-base-content">{title}</h1>
                <p className="text-base-content/70">
                    {text || "Puede que el enlace se haya cambiado. Pide a quien te lo mandó que te pase el nuevo."}
                </p>
                {action}
            </div>
        </div>
    );
}

CatalogUnavailable.propTypes = {
    title: PropTypes.string,
    text: PropTypes.string,
    action: PropTypes.node,
};
